/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useState, type FocusEvent, type KeyboardEvent } from 'react';

import { keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import { ExitingPersistence, Motion } from '@atlaskit/motion';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textarea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { messages } from '../memory-modal-subscreen/messages';

export type AddMemoryCallbacks = {
	onSuccess?: () => void;
	onError?: () => void;
};

export interface MemorySubscreenFooterAddMemoryProps {
	/**
	 * Called when the user submits a new explicit memory.
	 */
	onAddMemory: (content: string, callbacks?: AddMemoryCallbacks) => void;
	/**
	 * Whether an explicit memory creation is in progress.
	 */
	isCreatingMemory?: boolean;
	/**
	 * Called after the submitted memory is created successfully.
	 */
	onAddSuccess?: () => void;
	/**
	 * Called after the submitted memory fails to be created.
	 */
	onAddError?: () => void;
}

const slideIn = keyframes({
	'0%': { transform: 'translateY(50%)' },
	'100%': { transform: 'translateY(0)' },
});

const slideOut = keyframes({
	'0%': { transform: 'translateY(0)' },
	'100%': { transform: 'translateY(50%)' },
});

const styles = cssMap({
	addMemoryFooter: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'flex-end',
		gap: token('space.100'),
		alignSelf: 'stretch',
		width: '100%',
		paddingTop: token('space.200'),
	},
	slideIn: {
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.out.practical'),
		animationName: `${slideIn}`,
	},
	slideOut: {
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.in.practical'),
		animationName: `${slideOut}`,
	},
	MemorySubscreenFooterAddMemory: {
		display: 'flex',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingTop: token('space.100'),
		alignItems: 'center',
		flex: '1 0 0',
		overflow: 'hidden',
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width.focused'),
		borderStyle: 'solid',
		borderColor: token('color.border.focused'),
		backgroundColor: token('color.background.input'),
		gap: token('space.050'),
	},
	MemorySubscreenFooterAddMemoryContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		width: '100%',
	},
	MemorySubscreenFooterAddMemoryInputContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: token('space.050'),
	},
	motionContainer: {
		width: '100%',
	},
});

export const MemorySubscreenFooterAddMemory = ({
	onAddMemory,
	isCreatingMemory = false,
	onAddSuccess,
	onAddError,
}: MemorySubscreenFooterAddMemoryProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [isAddingMemory, setIsAddingMemory] = useState(false);
	const [addMemoryText, setAddMemoryText] = useState('');

	const handleAddMemoryOpen = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'addExplicitMemory',
			source: 'memoryModalSubscreen',
		});
		setIsAddingMemory(true);
	}, [sendAnalyticsEvent]);

	const handleAddMemoryDismiss = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'cancelAddExplicitMemory',
			source: 'memoryModalSubscreen',
		});
		setAddMemoryText('');
		setIsAddingMemory(false);
	}, [sendAnalyticsEvent]);

	const handleAddMemorySubmit = useCallback(() => {
		const trimmed = addMemoryText.trim();
		if (!trimmed) {
			return;
		}
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'submitAddExplicitMemory',
			source: 'memoryModalSubscreen',
		});
		onAddMemory(trimmed, {
			onSuccess: () => {
				setAddMemoryText('');
				setIsAddingMemory(false);
				onAddSuccess?.();
			},
			onError: () => {
				onAddError?.();
			},
		});
	}, [addMemoryText, onAddMemory, onAddSuccess, onAddError, sendAnalyticsEvent]);

	const handleAddMemoryKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				e.nativeEvent.stopImmediatePropagation();
				handleAddMemoryDismiss();
			}
		},
		[handleAddMemoryDismiss],
	);

	const handleAddMemoryBlur = useCallback(
		(e: FocusEvent<HTMLDivElement>) => {
			if (isCreatingMemory) {
				return;
			}
			if (!e.currentTarget.contains(e.relatedTarget as Node)) {
				handleAddMemoryDismiss();
			}
		},
		[isCreatingMemory, handleAddMemoryDismiss],
	);

	const MemorySubscreenFooterAddMemoryLabel = formatMessage(
		messages.MemorySubscreenFooterAddMemoryPlaceholder,
	);

	return (
		<Box
			xcss={styles.addMemoryFooter}
			testId="memory-modal-subscreen-add-memory-input-wrapper"
			onBlur={handleAddMemoryBlur}
		>
			<ExitingPersistence exitThenEnter>
				{isAddingMemory ? (
					<Motion
						key="add-memory-input"
						enteringAnimationXcss={styles.slideIn}
						exitingAnimationXcss={styles.slideOut}
						xcss={styles.motionContainer}
					>
						<Box xcss={styles.MemorySubscreenFooterAddMemoryContainer}>
							<Box xcss={styles.MemorySubscreenFooterAddMemoryInputContainer}>
								<div css={styles.MemorySubscreenFooterAddMemory}>
									<Textarea
										autoFocus
										appearance="none"
										resize="none"
										minimumRows={1}
										value={addMemoryText}
										isCompact
										isDisabled={isCreatingMemory}
										onChange={(e) => setAddMemoryText(e.target.value)}
										onKeyDown={handleAddMemoryKeyDown}
										placeholder={MemorySubscreenFooterAddMemoryLabel}
										aria-label={MemorySubscreenFooterAddMemoryLabel}
										testId="memory-modal-subscreen-add-memory-input"
									/>
								</div>
								<Button appearance="subtle" onClick={handleAddMemoryDismiss}>
									{formatMessage(messages.cancelAddMemory)}
								</Button>
								<Button
									appearance="primary"
									onClick={handleAddMemorySubmit}
									isLoading={isCreatingMemory}
									isDisabled={isCreatingMemory || !addMemoryText.trim()}
									testId="memory-modal-subscreen-add-memory-submit"
								>
									{formatMessage(messages.confirmAddMemory)}
								</Button>
							</Box>
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.addMemoryHelperText)}
							</Text>
						</Box>
					</Motion>
				) : (
					<Motion
						key="add-memory-trigger"
						enteringAnimationXcss={styles.slideIn}
						exitingAnimationXcss={styles.slideOut}
					>
						<Button
							iconAfter={(iconProps) => <AddIcon {...iconProps} size="small" />}
							appearance="primary"
							onClick={handleAddMemoryOpen}
							testId="memory-modal-subscreen-add-memory-button"
						>
							{formatMessage(messages.addMemoryButton)}
						</Button>
					</Motion>
				)}
			</ExitingPersistence>
		</Box>
	);
};
